import React, { useState, forwardRef } from 'react';
import { st, classes } from './StarsRatingBar.st.css';
import { dataHooks, starValues } from '../constants';

import { Text } from '../../Text';

import { Favorite, FavoriteFilled } from '@wix/wix-ui-icons-common';
import { useIcons } from '../../WixDesignSystemIconThemeProvider';
import { useUniqueId } from '../../providers/useCollapse/utils';
import { semanticClassNames } from '../StarsRatingBar.semanticClassNames';
import { StarsRatingBarProps } from '../StarsRatingBar.types';
import { getRatingLabel, getStarSizePx } from '../utils/common';
import { classes as commonClasses } from '../../common/styles/visuallyHidden.st.css';
import { InjectedFocusableProps, withFocusable } from '../../common/Focusable';

const renderCaption = (
  { descriptionValues }: StarsRatingBarProps,
  value?: number,
) => {
  if (
    !Array.isArray(descriptionValues) ||
    descriptionValues.length !== 5 ||
    !value
  ) {
    return null;
  }

  return (
    <div className={classes.rateCaption}>
      <Text
        dataHook={dataHooks.ratingCaption}
        aria-hidden
        ellipsis
        size="small"
        weight="thin"
        secondary
      >
        {descriptionValues[value - 1]}
      </Text>
    </div>
  );
};

const InteractiveStarsRatingBar = forwardRef<
  HTMLInputElement,
  InjectedFocusableProps<StarsRatingBarProps>
>((props, ref) => {
  const {
    dataHook,
    className,
    value,
    onChange,
    focusableOnFocus,
    focusableOnBlur,
  } = props;
  const icons = useIcons('InteractiveStarsRatingBar', {
    Favorite,
    FavoriteFilled,
  });
  const name = useUniqueId(props.name);
  const [hoveredValue, setHoveredValue] = useState<number | null>(null);
  const visualValue = hoveredValue ?? value ?? 0;
  const valueToPassReference = value > 0 ? value : 1; // if value is 0, we pass the reference to the first star
  const sizePx = getStarSizePx(props);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(parseInt(event.currentTarget.value));
  };

  const createPointerEventHandler =
    (value: number) => (event: React.PointerEvent<HTMLElement>) => {
      if (event.pointerType === 'touch') {
        // Ignoring the touch hover, because it is sticky and results in bad UX.
        return;
      }

      switch (event.type) {
        case 'pointerenter':
          setHoveredValue(value);
          break;

        case 'pointerleave':
        case 'pointercancel':
          setHoveredValue(null);
          break;

        default:
          break;
      }
    };

  return (
    <div
      data-hook={dataHook}
      className={st(classes.root, classes.focusable, className)}
    >
      <div role="radiogroup" className={classes.starsContainer}>
        {starValues.map(starValue => {
          const filled = visualValue >= starValue;
          const checked = value === starValue;
          const Icon = filled ? icons.FavoriteFilled : icons.Favorite;
          const handlePointerEvent = createPointerEventHandler(starValue);

          return (
            <label
              key={starValue}
              data-hook={filled ? dataHooks.filledStar : dataHooks.emptyStar}
              data-index={starValue}
              className={st(
                classes.starContainer,
                semanticClassNames.buttonInteractive,
              )}
              onPointerEnter={handlePointerEvent}
              onPointerLeave={handlePointerEvent}
              onPointerCancel={handlePointerEvent}
            >
              <input
                type="radio"
                name={name}
                value={starValue}
                checked={checked}
                aria-label={getRatingLabel(props, starValue)}
                className={commonClasses.visuallyHidden}
                onChange={handleChange}
                ref={valueToPassReference === starValue ? ref : undefined}
                onFocus={focusableOnFocus}
                onBlur={focusableOnBlur}
              />
              <Icon
                aria-hidden
                className={st(
                  classes.star,
                  { filled },
                  filled
                    ? semanticClassNames.filledIcon
                    : semanticClassNames.emptyIcon,
                )}
                size={sizePx}
              />
            </label>
          );
        })}
      </div>
      {renderCaption(props, visualValue)}
    </div>
  );
});

const WrappedInteractiveStarsRatingBar = withFocusable(
  InteractiveStarsRatingBar,
);

export { WrappedInteractiveStarsRatingBar as InteractiveStarsRatingBar };
